import { ref, onMounted, onBeforeUnmount } from 'vue';

/**
 * Expõe a geometria da *visual viewport* — a área que o usuário realmente vê,
 * descontando o teclado virtual e a toolbar do Safari.
 *
 * Por que isto existe: no Safari iOS, `position: fixed` resolve contra o
 * **layout viewport**, que NÃO encolhe quando o teclado sobe (só a
 * `visualViewport` encolhe). Um bottom-sheet `fixed` ancorado no rodapé fica,
 * portanto, ATRÁS do teclado — e só se recoloca quando um `resize` da
 * visualViewport força um novo layout, daí o clássico "na segunda vez que abro
 * o teclado funciona".
 *
 * Quem tem input dentro de um painel `fixed` deve dimensionar o painel por
 * `height`/`offsetTop` daqui em vez de `inset: 0`.
 *
 * Coalescing em `requestAnimationFrame` e escuta de `resize` + `scroll`: o
 * mesmo padrão já validado no menu mobile (`MobileMenuBarV2`), porque o
 * `offsetTop` oscila a cada quadro durante o scroll com o teclado aberto.
 */
export function useViewportInset() {
  // Fallbacks de SSR / de quem não tem a API (Android antigo, desktop velho):
  // 0 e 0 fazem o consumidor cair no valor de CSS padrão dele.
  const height = ref(0);
  const width = ref(0);
  const offsetTop = ref(0);

  let raf = 0;

  function measure() {
    const vv = window.visualViewport;

    if (!vv) {
      return;
    }

    height.value = vv.height;
    width.value = vv.width;
    offsetTop.value = vv.offsetTop;
  }

  function schedule() {
    if (raf) {
      return;
    }

    raf = requestAnimationFrame(() => {
      raf = 0;
      measure();
    });
  }

  onMounted(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    measure();
    window.visualViewport.addEventListener('resize', schedule);
    window.visualViewport.addEventListener('scroll', schedule);
  });

  onBeforeUnmount(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    window.visualViewport.removeEventListener('resize', schedule);
    window.visualViewport.removeEventListener('scroll', schedule);

    if (raf) {
      cancelAnimationFrame(raf);
    }
  });

  return { height, width, offsetTop };
}
